import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useFinishedJob, useJob, type JobKind } from "../api/jobs";
import {
  useExtractAll,
  useExtractionRuns,
  useIndexPending,
  useIndexRuns,
  useRebuildIndex,
  useSyncAll,
  useSyncRuns,
  useExtractionOutput,
} from "../api/queries";
import type { AnyRun } from "../api/types";
import { JobNotice } from "../components/JobNotice";
import { ExtractionOutput, IndexFailures, JobInProgress, SyncChanges } from "../components/RunDetails";
import { ExtractionRunsTable, IndexRunsTable, SyncRunsTable } from "../components/RunTables";
import { SyncButton } from "../components/SyncDialog";
import { Panel } from "../components/Panel";
import { Button, ErrorBanner, Loading, Modal, PageHeader } from "../components/ui";

const TABS = [
  { id: "sync", label: "Sync" },
  { id: "extract", label: "Extraction" },
  { id: "index", label: "Indexing" },
] as const;
type Tab = (typeof TABS)[number]["id"];

// ?tab=sync|extract|index and ?run=<id>|job select what's shown, so the
// dashboard can deep-link to a specific run.
function useSelection() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab) || "sync";
  const runParam = params.get("run");
  const selected: number | "job" | null = runParam === "job" ? "job" : runParam ? Number(runParam) : null;
  const select = (run: number | "job" | null, nextTab: Tab = tab) => {
    const next: Record<string, string> = { tab: nextTab };
    if (run !== null) next.run = String(run);
    setParams(next, { replace: true });
  };
  return { tab, selected, select };
}

// When a job we were showing finishes, jump to the run its task produced
// (or the newest run, if the task didn't say).
function useFollowFinishedJob(kind: JobKind, active: boolean, runs: AnyRun[] | undefined, selected: number | "job" | null, select: (run: number) => void) {
  const finished = useFinishedJob(kind);
  const wasActive = useRef(active);
  useEffect(() => {
    if (wasActive.current && !active && selected === "job") {
      const target = finished?.runId ?? runs?.[0]?.id;
      if (target !== undefined) select(target);
    }
    wasActive.current = active;
  }, [active, finished, runs, selected, select]);
}

export function RunsPage() {
  const { tab, select } = useSelection();
  return (
    <div className="p-6">
      <PageHeader title="Pipeline runs" subtitle="Every sync, extraction and indexing run. Click a run for details. Jobs you start here are tracked until they finish." />
      <div className="mb-5 flex flex-wrap gap-2" role="tablist" aria-label="Pipeline stage">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => select(null, t.id)}
            className={`rounded-full px-5 py-2 text-sm font-medium transition ${
              tab === t.id
                ? "bg-brand text-white shadow-sm"
                : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "sync" && <SyncTab />}
      {tab === "extract" && <ExtractTab />}
      {tab === "index" && <IndexTab />}
    </div>
  );
}

function SyncTab() {
  const { selected, select } = useSelection();
  const runs = useSyncRuns();
  const job = useJob("sync");
  const syncAll = useSyncAll();
  useFollowFinishedJob("sync", !!job, runs.data, selected, select);

  // A clicked run (or the job this browser started) opens in a dialog rather
  // than under the table; the URL keeps the selection so deep-links reopen it.
  const selectedId = typeof selected === "number" ? selected : null;
  const run = selectedId !== null ? runs.data?.find((r) => r.id === selectedId) : undefined;

  return (
    <>
      <ErrorBanner error={syncAll.error} />
      <JobNotice kind="sync" />
      <Panel
        title="Sync runs"
        actions={<SyncButton sync={syncAll} variant="brand" disabled={!!job} onStarted={() => select("job")}>{job ? "Sync running…" : "Sync Now"}</SyncButton>}
      >
        <ErrorBanner error={runs.error} />
        {runs.isLoading ? <Loading /> : (
          <div className="overflow-x-auto">
            <SyncRunsTable
              runs={runs.data ?? []}
              job={job}
              selectedId={selected === "job" ? "job" : selectedId}
              onSelect={(r) => select(r.id)}
              onSelectJob={() => select("job")}
            />
          </div>
        )}
      </Panel>
      {selected === "job" && job && (
        <Modal title="Sync in progress" onClose={() => select(null)}>
          <JobInProgress job={job} noun="sync" />
        </Modal>
      )}
      {run && (
        <Modal wide title={`What changed in sync #${run.id}`} onClose={() => select(null)}>
          <SyncChanges run={run} />
        </Modal>
      )}
    </>
  );
}

function ExtractTab() {
  const { selected, select } = useSelection();
  const runs = useExtractionRuns();
  const job = useJob("extract");
  const extractAll = useExtractAll();
  useFollowFinishedJob("extract", !!job, runs.data?.runs, selected, select);
  const output = useExtractionOutput(runs.data?.runs);

  const selectedId = typeof selected === "number" ? selected : null;
  const run = selectedId !== null ? runs.data?.runs.find((r) => r.id === selectedId) : undefined;

  return (
    <>
      <ErrorBanner error={extractAll.error} />
      <JobNotice kind="extract" />
      <Panel
        title={<>Extraction runs {runs.data && <span className="ml-2 font-normal text-gray-500">{runs.data.pending_modules} modules waiting</span>}</>}
        actions={<Button variant="brand" loading={extractAll.isPending} disabled={!!job} onClick={() => { extractAll.mutate(); select("job"); }}>{job ? "Extraction running…" : "Extract pending"}</Button>}
      >
        <ErrorBanner error={runs.error} />
        {runs.isLoading ? <Loading /> : (
          <div className="overflow-x-auto">
            <ExtractionRunsTable runs={runs.data?.runs ?? []} job={job} selectedId={selectedId} onSelect={(r) => select(r.id)} />
          </div>
        )}
      </Panel>
      {selected === "job" && job && (
        <Modal title="Extraction in progress" onClose={() => select(null)}>
          <JobInProgress job={job} noun="extraction" />
        </Modal>
      )}
      {run && (
        <Modal wide title={`What extraction #${run.id} produced`} onClose={() => select(null)}>
          <ErrorBanner error={output.error} />
          {!output.error && <ExtractionOutput run={run} documents={output.byRun?.get(run.id)} />}
        </Modal>
      )}
    </>
  );
}

function IndexTab() {
  const { selected, select } = useSelection();
  const runs = useIndexRuns();
  const job = useJob("index");
  const indexPending = useIndexPending();
  const rebuild = useRebuildIndex();
  useFollowFinishedJob("index", !!job, runs.data?.runs, selected, select);

  const selectedId = typeof selected === "number" ? selected : null;
  const run = selectedId !== null ? runs.data?.runs.find((r) => r.id === selectedId) : undefined;

  return (
    <>
      <ErrorBanner error={indexPending.error || rebuild.error} />
      <JobNotice kind="index" />
      <Panel
        title={<>Index runs {runs.data && <span className="ml-2 font-normal text-gray-500">{runs.data.pending_documents} not indexed · {runs.data.pending_approval_documents} awaiting approval</span>}</>}
        actions={
          <>
            <Button
              variant="danger"
              loading={rebuild.isPending}
              disabled={!!job}
              onClick={() => {
                if (confirm("Drop every course index and re-embed all approved documents? The tutor will have gaps until this finishes.")) {
                  rebuild.mutate();
                  select("job");
                }
              }}
            >
              Rebuild all
            </Button>
            <Button variant="brand" loading={indexPending.isPending} disabled={!!job} onClick={() => { indexPending.mutate(); select("job"); }}>{job ? "Indexing…" : "Index approved"}</Button>
          </>
        }
      >
        <ErrorBanner error={runs.error} />
        {runs.isLoading ? <Loading /> : (
          <div className="overflow-x-auto">
            <IndexRunsTable runs={runs.data?.runs ?? []} job={job} selectedId={selectedId} onSelect={(r) => select(r.id)} />
          </div>
        )}
      </Panel>
      {selected === "job" && job && (
        <Modal title="Indexing in progress" onClose={() => select(null)}>
          <JobInProgress job={job} noun="index run" />
        </Modal>
      )}
      {run && (
        <Modal wide title={`Failures in index run #${run.id}`} onClose={() => select(null)}>
          <IndexFailures run={run} />
        </Modal>
      )}
    </>
  );
}
